// Dashboard on Home (M5 plan D11): the figures, each with a tab behind it, and the charts.
import { useOrgData } from './accountant/useOrgData.js';
import type { Api } from './api.js';
import {
  CashChart,
  MonthlyChart,
  RecurringRevenue,
  TopSpending,
  type TrendMonth,
} from './charts.js';
import { dollars } from './format.js';

interface Data {
  cash: { total: string; accounts: { name: string; balance: string }[] };
  stripe: { name: string; balance: string | null; mrr: Record<string, string> | null }[];
  mrrHistory: { month: string; mrr: Record<string, string> }[];
  gst: { period: string; net: string } | null;
  toSort: number;
}

export function Dashboard(props: {
  api: Api;
  orgId: string;
  open: (tab: 'money' | 'tax' | 'reports') => void;
}) {
  const { data } = useOrgData<Data>(props.api, props.orgId, '/dashboard');
  if (!data) return null;
  const gst = data.gst ? data.gst.net : null;
  return (
    <div className="tiles">
      <button type="button" className="tile" onClick={() => props.open('reports')}>
        <span className="muted">Cash in the bank</span>
        <strong>{dollars(data.cash.total)}</strong>
      </button>
      {gst !== null && (
        <button type="button" className="tile" onClick={() => props.open('tax')}>
          <span className="muted">GST so far, {data.gst?.period}</span>
          <strong>
            {gst.startsWith('-') ? `${dollars(gst.slice(1))} back` : `${dollars(gst)} to pay`}
          </strong>
        </button>
      )}
      <button type="button" className="tile" onClick={() => props.open('money')}>
        <span className="muted">To sort</span>
        <strong>{data.toSort === 0 ? 'Nothing' : `${data.toSort} transactions`}</strong>
      </button>
    </div>
  );
}

interface Trends {
  months: TrendMonth[];
  spending: { from: string; to: string; total: string; top: { name: string; amount: string }[] };
}

/** Charts under the to-do list: the last 12 months and this financial year's biggest spending. */
export function Insights(props: { api: Api; orgId: string }) {
  const { data } = useOrgData<Trends>(props.api, props.orgId, '/dashboard/trends');
  const summary = useOrgData<Data>(props.api, props.orgId, '/dashboard').data;
  if (!data) return null;
  const quiet = data.months.every((m) => m.income === '0.00' && m.spending === '0.00');
  const stripe = summary?.stripe ?? [];
  if (quiet && stripe.length === 0) return null;
  return (
    <section className="insights" aria-label="Your numbers over time">
      {stripe.length > 0 && (
        <div className="insight full">
          <RecurringRevenue accounts={stripe} history={summary?.mrrHistory ?? []} />
        </div>
      )}
      {!quiet && (
        <>
          <div className="insight full">
            <MonthlyChart months={data.months} />
          </div>
          <div className="insight">
            <CashChart months={data.months} />
          </div>
          <div className="insight">
            <TopSpending {...data.spending} />
          </div>
        </>
      )}
    </section>
  );
}
